import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PaywallModal from "@/components/PaywallModal";
import AdBanner from "@/components/AdBanner";

export default function Community() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sort, setSort] = useState("newest");
  const [worlds, setWorlds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paywall, setPaywall] = useState(false);

  useEffect(() => {
    if (!user?.premium) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/community-worlds?sort=${sort}`);
        setWorlds(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load community");
      } finally {
        setLoading(false);
      }
    })();
  }, [sort, user?.premium]);

  const play = async (w) => {
    try {
      const { data } = await api.post("/adventures/start", {
        world_id: w.world_id,
        version_id: "v_default",
        hard_mode: false,
      });
      navigate(`/play/${data.adventure.adventure_id}`);
    } catch (e) {
      if (e.response?.status === 402) setPaywall(true);
      else toast.error(e.response?.data?.detail || "Could not start");
    }
  };

  const rate = async (w, stars) => {
    try {
      const { data } = await api.post(`/community-worlds/${w.world_id}/rate`, { rating: stars });
      setWorlds(worlds.map((x) => (x.world_id === w.world_id ? data : x)));
      toast.success(`Rated ${stars} ★`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rate failed");
    }
  };

  return (
    <div data-testid="community-page" className="space-y-6">
      <AdBanner slot="community-top" format="horizontal" />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[0.6rem] uppercase tracking-[0.4em] gold mb-1">Community Archive</div>
          <h2 className="font-serif-display text-2xl sm:text-3xl">Worlds forged by other Sovereigns</h2>
        </div>
        <div className="flex gap-1 font-mono text-[0.65rem] uppercase tracking-widest">
          {[
            ["newest", "Newest"],
            ["most_played", "Most Played"],
            ["top_rated", "Top Rated"],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`sort-${k}`}
              onClick={() => setSort(k)}
              className={`px-3 py-2 min-h-[44px] border rounded-sm ${
                sort === k ? "border-amber-500 gold" : "border-stone-700 text-stone-400 hover:border-amber-500/50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {!user?.premium ? (
        <div className="tome-card rounded-sm p-8 text-center space-y-3">
          <div className="font-serif-display text-xl">The Community Archive is a Sovereign privilege</div>
          <p className="font-prose text-stone-400">Ascend to browse, play, and rate worlds crafted by other Sovereigns.</p>
          <button className="btn-gold" onClick={() => setPaywall(true)}>Ascend</button>
        </div>
      ) : loading ? (
        <div className="font-mono text-xs text-stone-500 animate-pulse">Loading the archive...</div>
      ) : worlds.length === 0 ? (
        <div className="tome-card rounded-sm p-8 text-center font-serif-body text-stone-400">
          No worlds have been published yet. Be the first — forge one in <span className="gold">My Worlds</span>.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {worlds.map((w) => (
            <div key={w.world_id} data-testid={`community-card-${w.world_id}`} className="tome-card rounded-sm p-5 flex flex-col gap-3">
              <div>
                <div className="font-mono text-[0.6rem] uppercase tracking-widest gold mb-1">{w.mechanics_style.replace(/_/g, " ")}</div>
                <div className="font-serif-display text-lg leading-tight">{w.title}</div>
                <div className="font-serif-body italic text-stone-400 text-sm">{w.tagline}</div>
              </div>
              <div className="font-prose text-stone-300 text-sm leading-relaxed line-clamp-3 flex-1">{w.lore}</div>
              <div className="flex items-center justify-between font-mono text-[0.6rem] uppercase tracking-widest text-stone-500 border-t border-amber-500/10 pt-2">
                <span>
                  by {w.creator_name}
                  {w.creator_is_founder && <span className="gold ml-1" title="Founding Sovereign">✦</span>}
                </span>
                <span>{w.play_count} plays</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex gap-0 sm:gap-1 items-center" data-testid={`rating-${w.world_id}`}>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      onClick={() => rate(w, s)}
                      className={`text-lg leading-none w-8 h-10 flex items-center justify-center ${s <= Math.round(w.rating_avg) ? "gold" : "text-stone-700"} hover:scale-110 transition-transform`}
                      title={`Rate ${s}`}
                    >
                      ★
                    </button>
                  ))}
                  <span className="font-mono text-[0.65rem] text-stone-500 ml-1 self-center">{w.rating_avg || "—"} ({w.rating_count})</span>
                </div>
                <button data-testid={`play-${w.world_id}`} onClick={() => play(w)} className="btn-gold">Play</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {paywall && <PaywallModal reason="world" onClose={() => setPaywall(false)} />}
    </div>
  );
}
